import {Col, DSIconButton, DSIconEye} from "@cdx/ds";
import {VisionBoardNode} from "../../../cdx-models/utils/extended-fields";
import {NodeBushEntry} from "../useVisionBoardData";
import {VisionBoardBag} from "../canvas-utils";
import {useCanvasNode} from "./useCanvasNode";
import {MaybeInaccessibleCard} from "../../../components/Card";
import {useInstance, useRoot} from "../../../lib/mate/mate-utils";
import {MaybeInaccessibleChildCardLane} from "../../workflows/ChildCardLane";
import {TooltipForChild} from "@cdx/common";
import {CARD_LANE_HEIGHT} from "../../workflows/workflows.css";
import {CARD_HEIGHT, CARD_WIDTH} from "../../../components/Card/card.css";

const ModeButton = ({
  nodeData,
  bag,
  node,
}: {
  nodeData: VisionBoardNode & {type: "card"};
  bag: VisionBoardBag;
  node: NodeBushEntry;
}) => {
  const handleClick = () => {
    if (nodeData.value.mode === "micro") {
      bag.store.doc.transact(() => {
        node.node.set("value", {...nodeData.value, mode: "mini"});
        node.node.set("pos", {...node.node.get("pos"), w: CARD_WIDTH, h: CARD_HEIGHT});
      });
    } else {
      bag.store.doc.transact(() => {
        node.node.set("value", {...nodeData.value, mode: "micro"});
        node.node.set("pos", {...node.node.get("pos"), w: 200, h: CARD_LANE_HEIGHT});
      });
    }
  };

  return (
    <TooltipForChild tooltip="Toggle View">
      <DSIconButton variant="secondary" icon={<DSIconEye />} onClick={() => handleClick()} />
    </TooltipForChild>
  );
};

type CardWidgetProps = {
  node: NodeBushEntry;
  nodeData: VisionBoardNode & {type: "card"};
  bag: VisionBoardBag;
};
const CardWidget = (props: CardWidgetProps) => {
  const {nodeData, node, bag} = props;
  const {elements, nodeRef, containerProps, isActive} = useCanvasNode({
    node,
    nodeData,
    bag,
    disableResize: true,
    menuItems: <ModeButton bag={bag} node={node} nodeData={nodeData} />,
    minDims: [40, 40],
  });
  const {id} = nodeData.value;
  const card = useInstance("card", id);
  const root = useRoot();

  const getContent = () => {
    switch (nodeData.value.mode) {
      case "micro": {
        const getRouteProps = () => {
          if (!isActive || !card) return null;
          const {getCardUrl, goToCard} = bag.arenaCtx.routes;
          return {goToCard, to: getCardUrl({card})};
        };
        return (
          <MaybeInaccessibleChildCardLane
            card={card}
            root={root}
            {...getRouteProps()}
            arenaCtxType={bag.arenaCtx.type}
          />
        );
      }
      default: {
        const getRouteProps = () => {
          if (!isActive || !card) return null;
          const {getCardUrl, goToCard} = bag.arenaCtx.routes;
          return {getCardUrl, goToCard};
        };
        return (
          <MaybeInaccessibleCard
            card={card}
            root={root}
            noAnimation
            cardContainerKey={node.id}
            {...getRouteProps()}
          />
        );
      }
    }
  };

  return (
    <Col {...containerProps} ref={nodeRef} rounded={4}>
      {getContent()}
      {elements}
    </Col>
  );
};

export default CardWidget;
